import { isProduction } from '@zapier/common-utils';
import attachAttributionParams from './middleware/attachAttributionParams';
import attachWindowParams from './middleware/attachWindowParams';
import createMaybeAddUserId from './middleware/createMaybeAddUserId';
import createTrackingFunctions from './createTrackingFunctions';
import environmentParams from './middleware/environmentParams';
import eventDebugger from './middleware/eventDebugger';
import sendToBackend from './middleware/sendToBackend';
import sendToGA from './middleware/sendToGA';
import utmParams from './middleware/utmParams';
import { trackingScopeApi } from './trackingScope';
import { TrackingEvent, TrackingFunctions, TrackingMiddleware } from './types';
/**
 * Creates a middleware chain that can be used to fire tracking events.
 *
 * Supports passing extra middleware that will fire after a collection of
 * default middleware which retrieves attribution utm params, environmental
 * variables such as path, and the user id and before middleware that sends
 * the values to the backend and google analytics.
 *
 * Example usage:
 *
 * export const { trackEvent } = createEventTracker([
 *     extraMiddleware,
 *   ],
 * );
 *
 * trackEvent('hello-world', { foo: 'bar' })
 */

type ContextType = { [s: string]: any };

const createEventTracker = (
  customMiddleware: Array<TrackingMiddleware> = []
): TrackingFunctions => {
  const middleware = (isProduction()
    ? [
        attachWindowParams,
        attachAttributionParams,
        utmParams,
        createMaybeAddUserId,
        ...customMiddleware,
        environmentParams,
        sendToBackend,
        sendToGA,
      ]
    : [
        attachWindowParams,
        attachAttributionParams,
        utmParams,
        ...customMiddleware,
        environmentParams,
        eventDebugger,
      ]
  ).map(ware => ware(trackingScopeApi));
  return createTrackingFunctions(async (eventName, metaData) => {
    let context: ContextType = {};
    let isCancelled = false;

    const setIsCancelled = () => {
      isCancelled = true;
    };

    const applyContext = (newCtx: ContextType) => {
      context = {
        ...context,
        ...newCtx,
      };
    };

    const _deprecatedPropertiesInRoot: Set<string> = new Set();

    const _deprecatedSetProprtiesInRoot = (properties: any) => {
      properties.forEach((property: any) => {
        _deprecatedPropertiesInRoot.add(property);
      });
    };

    const _deprecatedBuildProprtiesInRoot = () => {
      const rootContext: { [s: string]: any } = {};

      _deprecatedPropertiesInRoot.forEach(key => {
        rootContext[key] = context[key];
      });

      return rootContext;
    };

    const buildEventApi = (): TrackingEvent => ({
      name: eventName,
      data: {
        ...metaData,
      },
      context: {
        ...context,
      },
      apply: applyContext,
      cancel: setIsCancelled,
      _deprecatedSetProprtiesInRoot,
      _deprecatedBuildProprtiesInRoot,
    });

    try {
      for (const promise of middleware) {
        const event: TrackingEvent = buildEventApi();
        await promise(event);

        if (isCancelled) {
          break;
        }
      }
    } catch (e) {
      console.error(
        JSON.stringify({
          message: 'Unable to track event: ',
          eventName,
          data: metaData,
          context,
        })
      );
    }
  });
};

export default createEventTracker;
